/**
 * Word List Words View
 *
 * Admin view for managing words within a specific word list.
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useWordList } from '../services/wordListsApi';
import { useWordsInList, useRemoveWordFromList, useReorderWords } from '../services/wordsApi';
import { SortableWordsTable } from '../components/SortableWordsTable';
import { ExtendWordListDialog } from '../components/ExtendWordListDialog';
import { ImportWordsCsvDialog } from '../components/ImportWordsCsvDialog';
import type { WordInList, WordOrderItem } from '../types/word.types';

export function WordListWordsView() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const pageSize = 20;

  const { data: wordList, isLoading: isLoadingList, error: listError } = useWordList(listId!);

  const {
    data: wordsData,
    isLoading: isLoadingWords,
    error: wordsError,
  } = useWordsInList(listId!, page, pageSize);

  const removeWord = useRemoveWordFromList();
  const reorderWords = useReorderWords();

  const handleReorderWords = (wordOrders: WordOrderItem[]) => {
    reorderWords.mutate(
      { listId: listId!, data: { word_orders: wordOrders } },
      {
        onError: (error) => {
          toast.error(`Failed to reorder words: ${(error as Error).message}`);
        },
      }
    );
  };

  const handleRemoveWord = (word: WordInList) => {
    if (
      !confirm(
        `Are you sure you want to remove "${word.word}" from this list? The word will remain in the database.`
      )
    ) {
      return;
    }

    removeWord.mutate(
      { listId: listId!, word: word.word, languageCode: word.languageCode, cardId: word.cardId },
      {
        onSuccess: () => {
          toast.success(`Word "${word.word}" removed from list`);
        },
        onError: (error) => {
          toast.error(`Failed to remove word: ${(error as Error).message}`);
        },
      }
    );
  };

  const handleEditWord = (word: WordInList) => {
    navigate(
      `/words/${encodeURIComponent(word.word)}/${word.languageCode}/edit?cardId=${word.cardId}`
    );
  };

  const handleAddWord = () => {
    navigate(`/words/new?listId=${listId}&lang=${wordList?.languageCode}`);
  };

  if (isLoadingList || isLoadingWords) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (listError || wordsError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading data</p>
          <p className="text-gray-600">
            {(listError as Error)?.message || (wordsError as Error)?.message}
          </p>
        </div>
      </div>
    );
  }

  if (!wordList) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Word list not found</p>
          <Link to="/words/lists" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
            Back to Word Lists
          </Link>
        </div>
      </div>
    );
  }

  const totalPages = wordsData ? Math.ceil(wordsData.total / pageSize) : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/words/lists" className="text-gray-500 hover:text-gray-700">
              Word Lists
            </Link>
            <span className="text-gray-400">/</span>
            <Link
              to={`/words/lists/${wordList.id}/edit`}
              className="text-gray-500 hover:text-gray-700"
            >
              {wordList.name}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">Words</span>
          </div>
          <h1 className="text-3xl font-bold">{wordList.name} - Words</h1>
          <p className="text-gray-600 mt-1">{wordsData?.total ?? 0} words in this list</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportDialog(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Import CSV
          </button>
          <button
            onClick={() => setShowExtendDialog(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
          >
            Extend with AI
          </button>
          <button
            onClick={handleAddWord}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Add Word
          </button>
        </div>
      </div>

      {/* Words Table */}
      {wordsData && wordsData.items.length > 0 ? (
        <SortableWordsTable
          words={wordsData.items}
          onEdit={handleEditWord}
          onRemove={handleRemoveWord}
          onReorder={handleReorderWords}
          isRemoving={removeWord.isPending}
          isReordering={reorderWords.isPending}
        />
      ) : (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 text-lg mb-4">No words in this list yet.</p>
          <button
            onClick={handleAddWord}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Add Your First Word
          </button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {(page - 1) * pageSize + 1} to{' '}
            {Math.min(page * pageSize, wordsData?.total ?? 0)} of {wordsData?.total ?? 0} words
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Extend Word List Dialog */}
      {showExtendDialog && wordList && (
        <ExtendWordListDialog
          listId={listId!}
          listName={wordList.name}
          currentWordCount={wordsData?.total ?? 0}
          onClose={() => setShowExtendDialog(false)}
          onSuccess={() => {
            // The useExtendWordList hook already invalidates queries
          }}
        />
      )}

      {/* Import Words from CSV Dialog */}
      {showImportDialog && wordList && (
        <ImportWordsCsvDialog
          listId={listId!}
          listName={wordList.name}
          onClose={() => setShowImportDialog(false)}
          onSuccess={() => {
            // The useImportWordsFromCsv hook already invalidates queries
          }}
        />
      )}
    </div>
  );
}
